import { time } from "@/lib/time";

export type SafeTelemetryPrimitive = boolean | number | string;
export type SafeTelemetryValue = SafeTelemetryPrimitive | SafeTelemetryPrimitive[];

const MAX_TELEMETRY_STRING_LENGTH = 120;
const TELEMETRY_ELLIPSIS = "...";

/**
 * Buckets a result size so analytics stay queryable without exact payload counts.
 */
export function bucketTelemetryCount({ count }: { count?: number }): string | undefined {
  if (typeof count !== "number" || !Number.isFinite(count) || count < 0) {
    return undefined;
  }

  if (count === 0) {
    return "0";
  }

  if (count === 1) {
    return "1";
  }

  if (count <= 5) {
    return "2_to_5";
  }

  if (count <= 10) {
    return "6_to_10";
  }

  if (count <= 25) {
    return "11_to_25";
  }

  if (count <= 100) {
    return "26_to_100";
  }

  return "gt_100";
}

/**
 * Classifies a finished page load for analytics: an error wins over an empty result.
 */
export function getTelemetryLoadStatus({
  hasError,
  hasResults,
}: {
  hasError: boolean;
  hasResults: boolean;
}): "empty" | "error" | "success" {
  if (hasError) return "error";
  return hasResults ? "success" : "empty";
}

/**
 * Buckets elapsed time for low-cardinality product analytics while preserving a
 * coarse feel for whether a request was fast or slow.
 */
export function bucketDurationMs({ durationMs }: { durationMs?: number }): string | undefined {
  if (typeof durationMs !== "number" || !Number.isFinite(durationMs) || durationMs < 0) {
    return undefined;
  }

  if (durationMs < 250) {
    return "lt_250ms";
  }

  if (durationMs < 1_000) {
    return "250ms_to_999ms";
  }

  if (durationMs < 3_000) {
    return "1s_to_3s";
  }

  if (durationMs < 10_000) {
    return "3s_to_10s";
  }

  return "gte_10s";
}

/**
 * Converts a date-only range into a coarse bucket that can be shared by server
 * and client telemetry.
 */
export function getDateRangeDaysBucket({
  start,
  end,
}: {
  start?: string;
  end?: string;
}): string | undefined {
  if (!start || !end) {
    return undefined;
  }

  const startDate = time(start);
  const endDate = time(end);
  if (!startDate.isValid() || !endDate.isValid()) {
    return undefined;
  }

  const totalDays = endDate.diff(startDate, "day") + 1;
  if (!Number.isFinite(totalDays) || totalDays <= 0) {
    return undefined;
  }

  if (totalDays <= 7) {
    return "within_7_days";
  }

  if (totalDays <= 30) {
    return "within_30_days";
  }

  if (totalDays <= 90) {
    return "within_90_days";
  }

  if (totalDays <= 365) {
    return "within_1_year";
  }

  return "gt_1_year";
}

/**
 * Keeps telemetry strings short and free of surrounding whitespace.
 */
export function sanitizeTelemetryString(value: string | undefined): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return undefined;
  }

  if (trimmedValue.length <= MAX_TELEMETRY_STRING_LENGTH) {
    return trimmedValue;
  }

  if (MAX_TELEMETRY_STRING_LENGTH <= TELEMETRY_ELLIPSIS.length) {
    return TELEMETRY_ELLIPSIS.slice(0, MAX_TELEMETRY_STRING_LENGTH);
  }

  return `${trimmedValue.slice(0, MAX_TELEMETRY_STRING_LENGTH - TELEMETRY_ELLIPSIS.length)}${TELEMETRY_ELLIPSIS}`;
}

/**
 * Normalizes arbitrary telemetry payload values to bounded primitives.
 */
export function sanitizeTelemetryValue(value: unknown): SafeTelemetryValue | undefined {
  if (typeof value === "string") {
    return sanitizeTelemetryString(value);
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (!Array.isArray(value)) {
    return undefined;
  }

  const sanitizedValues = value
    .map((item) => sanitizeTelemetryValue(item))
    .flatMap((item) => {
      if (Array.isArray(item)) return item;
      return item === undefined ? [] : [item];
    });

  return sanitizedValues.length > 0 ? sanitizedValues : undefined;
}

/**
 * Sanitizes a telemetry context object against an optional allowlist.
 */
export function sanitizeTelemetryContext({
  context,
  allowedKeys,
}: {
  context?: Record<string, unknown>;
  allowedKeys?: readonly string[];
}): Record<string, SafeTelemetryValue> | undefined {
  if (!context) {
    return undefined;
  }

  const allowedKeySet = allowedKeys ? new Set<string>(allowedKeys) : undefined;
  const sanitizedContext = Object.entries(context).reduce<Record<string, SafeTelemetryValue>>(
    (accumulator, [key, value]) => {
      if (allowedKeySet && !allowedKeySet.has(key)) {
        return accumulator;
      }

      const sanitizedValue = sanitizeTelemetryValue(value);
      if (sanitizedValue !== undefined) {
        accumulator[key] = sanitizedValue;
      }

      return accumulator;
    },
    {},
  );

  return Object.keys(sanitizedContext).length > 0 ? sanitizedContext : undefined;
}

/**
 * Pulls a stable, queryable error type out of unknown thrown values.
 */
export function extractTelemetryErrorType({ error }: { error: unknown }): string {
  if (error instanceof Error) {
    return sanitizeTelemetryString(error.name) ?? "Error";
  }

  if (typeof error === "string") {
    return "StringError";
  }

  if (typeof error === "object" && error !== null) {
    return "ObjectError";
  }

  return "UnknownError";
}

/**
 * Reads a best-effort HTTP status from common request error shapes.
 */
export function extractTelemetryHttpStatus({ error }: { error: unknown }): number | undefined {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  const candidateValues = [
    (error as { status?: unknown }).status,
    (error as { statusCode?: unknown }).statusCode,
    (error as { response?: { status?: unknown } }).response?.status,
  ];

  for (const candidateValue of candidateValues) {
    if (typeof candidateValue === "number" && Number.isFinite(candidateValue)) {
      return candidateValue;
    }

    if (typeof candidateValue === "string") {
      const parsedValue = Number.parseInt(candidateValue, 10);
      if (Number.isFinite(parsedValue)) {
        return parsedValue;
      }
    }
  }

  return undefined;
}

/**
 * Extracts PHI-safe error metadata that can be reused in analytics, logs, and Sentry.
 */
function getOperationOutcomeIssues({ error }: { error: unknown }): unknown[] {
  if (!error || typeof error !== "object") {
    return [];
  }

  const outcome = (error as { outcome?: { issue?: unknown } }).outcome;
  if (!outcome || !Array.isArray(outcome.issue)) {
    return [];
  }

  return outcome.issue;
}

/**
 * Extracts bounded Medplum OperationOutcome metadata without raw diagnostics.
 */
function extractOperationOutcomeMetadata({
  error,
}: {
  error: unknown;
}): Record<string, SafeTelemetryValue> {
  const issues = getOperationOutcomeIssues({ error });
  if (issues.length === 0) {
    return {};
  }

  const issueCodes = issues
    .map((issue) => (issue as { code?: unknown }).code)
    .filter((code): code is string => typeof code === "string")
    .map((code) => sanitizeTelemetryString(code))
    .filter((code): code is string => Boolean(code))
    .slice(0, 5);
  const issueSeverities = issues
    .map((issue) => (issue as { severity?: unknown }).severity)
    .filter((severity): severity is string => typeof severity === "string")
    .map((severity) => sanitizeTelemetryString(severity))
    .filter((severity): severity is string => Boolean(severity))
    .slice(0, 5);

  const metadata: Record<string, SafeTelemetryValue> = {
    medplum_issue_count: issues.length,
  };

  if (issueCodes.length > 0) {
    metadata.medplum_issue_codes = issueCodes;
  }

  if (issueSeverities.length > 0) {
    metadata.medplum_issue_severities = issueSeverities;
  }

  return metadata;
}

export function extractTelemetryErrorMetadata({
  error,
}: {
  error: unknown;
}): Record<string, SafeTelemetryValue> {
  const payload: Record<string, SafeTelemetryValue> = {
    error_type: extractTelemetryErrorType({ error }),
  };

  const httpStatus = extractTelemetryHttpStatus({ error });
  if (httpStatus !== undefined) {
    payload.http_status = httpStatus;
  }

  return {
    ...payload,
    ...extractOperationOutcomeMetadata({ error }),
  };
}

/**
 * Converts unknown thrown values into a stable Error instance without relying on
 * raw upstream messages.
 */
export function normalizeTelemetryError({
  error,
  fallbackMessage,
}: {
  error: unknown;
  fallbackMessage: string;
}): Error {
  if (error instanceof Error) {
    const normalizedError = new Error(fallbackMessage);
    normalizedError.name = sanitizeTelemetryString(error.name) ?? "Error";
    normalizedError.stack = error.stack;
    return normalizedError;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return new Error(fallbackMessage);
  }

  return new Error(fallbackMessage);
}
